import { useState } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "@/lib/safeToast";

function safeDestination(value: string | null) {
  return value?.startsWith("/") && !value.startsWith("//") ? value : "/meu-espaco";
}

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const destination = safeDestination(searchParams.get("next"));

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    if (password.length < 10) {
      toast.error("Use uma senha com pelo menos 10 caracteres.");
      return;
    }
    if (password !== confirmation) {
      toast.error("As duas senhas não são iguais.");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Senha atualizada com segurança.");
      navigate(destination, { replace: true });
    } catch (error: any) {
      toast.error(error?.message ?? "Não foi possível atualizar a senha.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="min-h-[100dvh] grid place-items-center text-sm text-muted-foreground">Validando o link…</div>;
  }

  if (!session) {
    return (
      <div className="min-h-[100dvh] bg-background px-4 py-[max(24px,env(safe-area-inset-top))] grid place-items-center">
        <Card className="w-full max-w-md rounded-3xl p-6 shadow-xl">
          <h1 className="text-xl font-semibold">Link inválido ou expirado</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Solicite um novo e-mail de recuperação. Por segurança, cada link só funciona por tempo limitado.
          </p>
          <Button className="mt-5 w-full" onClick={() => navigate("/auth", { replace: true })}>
            Voltar ao acesso
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background text-foreground px-4 py-[max(24px,env(safe-area-inset-top))] pb-[max(24px,env(safe-area-inset-bottom))] grid place-items-center">
      <Card className="w-full max-w-md overflow-hidden rounded-3xl border-border/70 shadow-xl">
        <div className="bg-gradient-to-br from-primary/15 via-background to-background px-5 pt-6 pb-5 sm:px-8 sm:pt-8">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">PsicoNorte</div>
              <h1 className="text-2xl font-semibold leading-tight">Criar nova senha</h1>
            </div>
          </div>
          <p className="mt-4 text-sm leading-6 text-muted-foreground">
            Escolha uma senha nova e exclusiva. Ela será atualizada diretamente no Supabase e não ficará armazenada no aplicativo.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 px-5 pb-6 sm:px-8 sm:pb-8">
          <div className="space-y-1.5">
            <Label htmlFor="new-password">Nova senha</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                className="h-12 pl-10 text-base"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                minLength={10}
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">Repetir nova senha</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                className="h-12 pl-10 text-base"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                minLength={10}
                required
              />
            </div>
          </div>

          <Button type="submit" disabled={saving} className="h-12 w-full text-base">
            {saving ? "Atualizando…" : "Salvar nova senha"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
